import React, { useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputSwitch } from 'primereact/inputswitch';
import { InputTextarea } from 'primereact/inputtextarea';
import { Chips } from 'primereact/chips';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../Layout/Layout';
import { formatDate, formatDateTime } from '../../utils/datetime';
import FormField from '../SharedComponents/Forms/FormField';
import { useCustomToast } from '../Contexts/ToastContext';
import {
  useGetCustomers,
  useGetCustomerStats,
  useAddCustomer,
  useUpdateCustomer,
  useDeleteCustomer,
  useImportCustomers,
} from '../../queries/customers';
import { useInHub } from '../../nav/useInHub';

const SOURCE_SEVERITY = {
  birthday_event: 'warning',
  spin_win: 'success',
  happy_hour: 'info',
  loyalty: 'success',
  manual: null,
  import: 'secondary',
};

/**
 * Machine-readable 'Y-m-d' slice, for the edit form's Calendar seed and the CSV
 * export. NOT for display — use formatDate() so the venue sees its own locale.
 * (birthday is a date-only field; the API sends it as UTC midnight ISO.)
 */
const dateOnly = (v) => (v ? String(v).slice(0, 10) : '');
const toYmd = (d) => {
  if (!d) return null;
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return null;
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const EMPTY = {
  name: '', phone: '', email: '', birthday: null, source: 'manual',
  gender: null, preferred_language: 'ar', notes: '', tags: [], is_subscribed: true,
};

export default function CustomersIndex() {
  const { t } = useTranslation();
  const inHub = useInHub();
  const { showToast } = useCustomToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const segment = searchParams.get('segment') || null;   // badge-driven segment (from a profile chip)

  const [page, setPage] = useState(0);          // 0-indexed for DataTable
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [source, setSource] = useState(null);

  const params = useMemo(
    () => ({
      page: page + 1,
      per_page: perPage,
      search: search || undefined,
      source: source || undefined,
      segment: segment || undefined,
    }),
    [page, perPage, search, source, segment]
  );

  const { data: list, isLoading, isFetching } = useGetCustomers(params);
  const { data: stats } = useGetCustomerStats();
  const addCustomer = useAddCustomer();
  const updateCustomer = useUpdateCustomer();
  const deleteCustomer = useDeleteCustomer();
  const importCustomers = useImportCustomers();

  const rows = list?.data ?? [];
  const totalRecords = list?.pagination?.total ?? 0;

  const fileRef = useRef(null);

  /* ── dialog state ─────────────────────────────────────────────────── */
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { control, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues: EMPTY });

  const openAdd = () => {
    setEditingId(null);
    reset(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    reset({
      name: row.name ?? '',
      phone: row.phone ?? '',
      email: row.email ?? '',
      birthday: row.birthday ? new Date(dateOnly(row.birthday)) : null,
      source: row.source ?? 'manual',
      gender: row.gender ?? null,
      preferred_language: row.preferred_language ?? 'ar',
      notes: row.notes ?? '',
      tags: row.tags ?? [],
      is_subscribed: row.is_subscribed ?? true,
    });
    setDialogOpen(true);
  };

  const onSubmit = async (form) => {
    const payload = { ...form, birthday: toYmd(form.birthday) };
    try {
      if (editingId) {
        await updateCustomer.mutateAsync({ id: editingId, ...payload });
        showToast('success', t('customers.title'), t('customers.saved'));
      } else {
        await addCustomer.mutateAsync(payload);
        showToast('success', t('customers.title'), t('customers.added'));
      }
      setDialogOpen(false);
    } catch (_) { /* toast handled in hook */ }
  };

  const confirmDelete = (row) => {
    confirmDialog({
      message: t('customers.delete_confirm', { name: row.name || row.phone || t('customers.this_customer') }),
      header: t('customers.delete_title'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('customers.delete'),
      rejectLabel: t('customers.cancel'),
      accept: async () => {
        try {
          await deleteCustomer.mutateAsync(row.id);
          showToast('success', t('customers.title'), t('customers.deleted'));
        } catch (_) { /* handled */ }
      },
    });
  };

  /* ── CSV import / export ──────────────────────────────────────────── */
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const res = (await importCustomers.mutateAsync(file)).data;
      const r = res?.data ?? res;
      showToast('success', t('customers.title'),
        t('customers.import_done', { imported: r.imported ?? 0, updated: r.updated ?? 0, skipped: r.skipped ?? 0 }));
    } catch (_) { /* handled */ }
  };

  const exportCsv = () => {
    const headers = ['name', 'phone', 'email', 'birthday', 'source', 'visit_count', 'last_visit', 'is_subscribed'];
    const escape = (v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
    const lines = [headers.join(',')].concat(
      rows.map((c) => [
        c.name, c.phone, c.email, dateOnly(c.birthday), c.source,
        c.visit_count, formatDateTime(c.last_visit, ''), c.is_subscribed ? 'yes' : 'no',
      ].map(escape).join(','))
    );
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `customers_${toYmd(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  /* ── column templates ─────────────────────────────────────────────── */
  const sourceBody = (row) => (
    <Tag value={t(`customers.source.${row.source}`)} severity={SOURCE_SEVERITY[row.source] ?? null} rounded />
  );
  const subscribedBody = (row) =>
    row.is_subscribed
      ? <i className="pi pi-check-circle" style={{ color: 'var(--green-500)' }} title={t('customers.subscribed')} />
      : <i className="pi pi-times-circle" style={{ color: 'var(--surface-400)' }} title={t('customers.unsubscribed')} />;
  const actionsBody = (row) => (
    <div className="flex gap-2">
      <Button icon="pi pi-user" rounded text severity="secondary"
        onClick={(e) => { e.stopPropagation(); navigate(`/customers/${row.id}`); }} aria-label={t('customers.view_profile')} tooltip={t('customers.view_profile')} />
      <Button icon="pi pi-pencil" rounded text severity="secondary" onClick={(e) => { e.stopPropagation(); openEdit(row); }} aria-label="edit" />
      <Button icon="pi pi-trash" rounded text severity="danger" onClick={(e) => { e.stopPropagation(); confirmDelete(row); }} aria-label="delete" />
    </div>
  );

  const sourceOptions = [
    { label: t('customers.all_sources'), value: null },
    ...['birthday_event', 'spin_win', 'happy_hour', 'loyalty', 'manual', 'import'].map((s) => ({ label: t(`customers.source.${s}`), value: s })),
  ];
  const genderOptions = [
    { label: t('customers.gender_male'), value: 'male' },
    { label: t('customers.gender_female'), value: 'female' },
  ];
  const langOptions = [
    { label: 'العربية', value: 'ar' },
    { label: 'English', value: 'en' },
  ];
  const sourceFormOptions = ['manual', 'birthday_event', 'spin_win', 'happy_hour', 'import']
    .map((s) => ({ label: t(`customers.source.${s}`), value: s }));

  /* ── stats cards ──────────────────────────────────────────────────── */
  const statCards = [
    { key: 'total', icon: 'pi-users', value: stats?.total ?? 0, label: t('customers.stat_total') },
    { key: 'new', icon: 'pi-user-plus', value: stats?.new_this_month ?? 0, label: t('customers.stat_new_month') },
    { key: 'subscribed', icon: 'pi-envelope', value: stats?.subscribed ?? 0, label: t('customers.stat_subscribed') },
    { key: 'birthday', icon: 'pi-gift', value: stats?.with_birthday ?? 0, label: t('customers.stat_birthdays') },
  ];

  return (
    <Layout>
      <ConfirmDialog />
      <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onImportFile} />

      <div className="p-3 md:p-4">
        {/* Header */}
        <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3 mb-3">
          {!inHub && (
          <div>
            <h2 className="m-0">{t('customers.title')}</h2>
            <p className="m-0 mt-1 text-500">{t('customers.subtitle')}</p>
          </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <Button icon="pi pi-upload" label={t('customers.import_csv')} outlined
              onClick={() => fileRef.current?.click()} loading={importCustomers.isLoading} />
            <Button icon="pi pi-download" label={t('customers.export_csv')} outlined
              onClick={exportCsv} disabled={!rows.length} />
            <Button icon="pi pi-plus" label={t('customers.add_customer')} onClick={openAdd} />
          </div>
        </div>

        {/* Stats */}
        <div className="grid mb-2">
          {statCards.map((s) => (
            <div key={s.key} className="col-6 md:col-3">
              <div className="surface-card border-round p-3 flex align-items-center gap-3 h-full">
                <span className="flex align-items-center justify-content-center border-circle"
                  style={{ width: 44, height: 44, background: 'var(--primary-50, var(--surface-100))' }}>
                  <i className={`pi ${s.icon}`} style={{ fontSize: '1.2rem', color: 'var(--primary-color)' }} />
                </span>
                <div>
                  <div className="text-2xl font-bold">{s.value}</div>
                  <div className="text-500 text-sm">{s.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        {segment && (
          <div className="flex align-items-center gap-2 mb-3 p-3 border-round"
            style={{ background: 'var(--surface-100)', border: '1px solid var(--surface-300)', flexWrap: 'wrap' }}>
            <i className="pi pi-filter" />
            <span className="font-semibold">{t(`customers.segment.${segment}`)}</span>
            <span className="text-500">· {totalRecords}</span>
            {segment === 'unreachable'
              ? <span className="text-500 text-sm ml-2">{t('customers.unreachable_hint')}</span>
              : <Button label={t('customers.message_these')} icon="pi pi-send" size="small" className="ml-2"
                  onClick={() => navigate(`/marketing/campaigns?segment=${segment}`)} />}
            <Button label={t('customers.clear_segment')} icon="pi pi-times" text size="small"
              onClick={() => setSearchParams({})} />
          </div>
        )}

        <div className="flex flex-column sm:flex-row gap-2 mb-3">
          <span className="p-input-icon-left flex-1">
            <i className="pi pi-search" />
            <InputText
              className="w-full"
              placeholder={t('customers.search_placeholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); setSearch(searchInput.trim()); } }}
            />
          </span>
          <Dropdown
            value={source}
            options={sourceOptions}
            onChange={(e) => { setPage(0); setSource(e.value); }}
            placeholder={t('customers.all_sources')}
            className="w-full sm:w-14rem"
          />
        </div>

        {/* Table */}
        <DataTable
          value={rows}
          lazy
          paginator
          first={page * perPage}
          rows={perPage}
          totalRecords={totalRecords}
          onPage={(e) => { setPage(e.page); setPerPage(e.rows); }}
          rowsPerPageOptions={[25, 50, 100]}
          loading={isLoading || isFetching}
          dataKey="id"
          emptyMessage={t('customers.empty')}
          responsiveLayout="scroll"
          stripedRows
          onRowClick={(e) => navigate(`/customers/${e.data.id}`)}
          rowClassName={() => 'cursor-pointer'}
        >
          <Column field="name" header={t('customers.col_name')} body={(r) => r.name || <span className="text-400">—</span>} />
          <Column field="phone" header={t('customers.col_phone')} body={(r) => <span dir="ltr">{r.phone || '—'}</span>} />
          <Column field="email" header={t('customers.col_email')} body={(r) => <span dir="ltr">{r.email || '—'}</span>} />
          <Column field="birthday" header={t('customers.col_birthday')} body={(r) => formatDate(r.birthday)} />
          <Column field="source" header={t('customers.col_source')} body={sourceBody} />
          <Column field="visit_count" header={t('customers.col_visits')} body={(r) => r.visit_count ?? 0} />
          <Column field="last_visit" header={t('customers.col_last_visit')} body={(r) => formatDateTime(r.last_visit)} />
          <Column header={t('customers.col_subscribed')} body={subscribedBody} style={{ width: '5rem', textAlign: 'center' }} />
          <Column header="" body={actionsBody} style={{ width: '7rem' }} />
        </DataTable>
      </div>

      {/* Add / Edit dialog */}
      <Dialog
        header={editingId ? t('customers.edit_customer') : t('customers.add_customer')}
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        style={{ width: '32rem', maxWidth: '95vw' }}
        breakpoints={{ '640px': '95vw' }}
        dismissableMask
      >
        <form onSubmit={handleSubmit(onSubmit)} className="pt-2">
          <FormField icon="pi-user" label={t('customers.col_name')} htmlFor="name" error={errors.name?.message}>
            <Controller name="name" control={control}
              rules={{ validate: (v, f) => (v || f.phone || f.email) ? true : t('customers.identity_required') }}
              render={({ field }) => <InputText id="name" {...field} className={classNames('w-full', { 'p-invalid': errors.name })} />} />
          </FormField>

          <FormField icon="pi-phone" label={t('customers.col_phone')} htmlFor="phone" error={errors.phone?.message}>
            <Controller name="phone" control={control}
              render={({ field }) => <InputText id="phone" dir="ltr" {...field} className="w-full" />} />
          </FormField>

          <FormField icon="pi-envelope" label={t('customers.col_email')} htmlFor="email" error={errors.email?.message}>
            <Controller name="email" control={control}
              rules={{ pattern: { value: /^[\w.+-]+@[\w.-]+\.\w{2,}$/, message: t('customers.email_invalid') } }}
              render={({ field }) => <InputText id="email" dir="ltr" {...field} className={classNames('w-full', { 'p-invalid': errors.email })} />} />
          </FormField>

          <div className="grid">
            <div className="col-6">
              <FormField icon="pi-gift" label={t('customers.col_birthday')} htmlFor="birthday">
                <Controller name="birthday" control={control}
                  render={({ field }) => (
                    <Calendar id="birthday" value={field.value} onChange={(e) => field.onChange(e.value)}
                      dateFormat="yy-mm-dd" showIcon monthNavigator yearNavigator yearRange="1930:2025" className="w-full" />
                  )} />
              </FormField>
            </div>
            <div className="col-6">
              <FormField icon="pi-flag" label={t('customers.source_label')} htmlFor="source">
                <Controller name="source" control={control}
                  render={({ field }) => (
                    <Dropdown id="source" value={field.value} options={sourceFormOptions}
                      onChange={(e) => field.onChange(e.value)} className="w-full" />
                  )} />
              </FormField>
            </div>
          </div>

          {/* Staff-only enrichment */}
          <div className="text-500 text-sm mt-2 mb-2">{t('customers.staff_section')}</div>
          <div className="grid">
            <div className="col-6">
              <FormField icon="pi-users" label={t('customers.gender')} htmlFor="gender">
                <Controller name="gender" control={control}
                  render={({ field }) => (
                    <Dropdown id="gender" value={field.value} options={genderOptions} showClear
                      onChange={(e) => field.onChange(e.value)} placeholder="—" className="w-full" />
                  )} />
              </FormField>
            </div>
            <div className="col-6">
              <FormField icon="pi-comment" label={t('customers.language')} htmlFor="language">
                <Controller name="preferred_language" control={control}
                  render={({ field }) => (
                    <Dropdown id="language" value={field.value} options={langOptions}
                      onChange={(e) => field.onChange(e.value)} className="w-full" />
                  )} />
              </FormField>
            </div>
          </div>

          <FormField icon="pi-tags" label={t('customers.tags')} htmlFor="tags" hint={t('customers.tags_hint')}>
            <Controller name="tags" control={control}
              render={({ field }) => (
                <Chips id="tags" value={field.value} onChange={(e) => field.onChange(e.value)} className="w-full" />
              )} />
          </FormField>

          <FormField icon="pi-pencil" label={t('customers.notes')} htmlFor="notes">
            <Controller name="notes" control={control}
              render={({ field }) => <InputTextarea id="notes" {...field} rows={2} className="w-full" autoResize />} />
          </FormField>

          <div className="flex align-items-center gap-2 mb-3">
            <Controller name="is_subscribed" control={control}
              render={({ field }) => <InputSwitch checked={field.value} onChange={(e) => field.onChange(e.value)} />} />
            <span>{t('customers.is_subscribed')}</span>
          </div>

          <div className="flex justify-content-end gap-2">
            <Button type="button" label={t('customers.cancel')} text onClick={() => setDialogOpen(false)} />
            <Button type="submit" label={t('customers.save')} icon="pi pi-check"
              loading={addCustomer.isLoading || updateCustomer.isLoading} />
          </div>
        </form>
      </Dialog>
    </Layout>
  );
}
